import { useQuery } from '@tanstack/react-query'
import { WidgetPreview } from '@/components/widget-builder/WidgetPreview'
import type { Database } from '@/integrations/supabase/types/database'
import { supabase } from '@/integrations/supabase/client'
import { useParams } from 'react-router-dom'
import { useTranslation } from '@/hooks/useTranslation'

type Widget = Database['public']['Tables']['widgets']['Row'] & {
	enable_survey: boolean
	enable_bug_reporting: boolean
	enable_feature_requests: boolean
	enable_reviews: boolean
	header_title?: string
	primary_feedback_type?: 'nps' | 'csat' | 'custom' | 'testimonial'
	show_feedback_button: boolean
	feedback_button_text: string
	feedback_button_color: string
	feedback_button_text_color: string
	show_branding: boolean
	program_id: string
}

export default function EmbedWidget() {
	const { t } = useTranslation()
	const { id } = useParams()
	const widgetId = id || ''

	const { data: widget } = useQuery({
		queryKey: ['widget', widgetId],
		queryFn: async () => {
			const { data, error } = await supabase
				.from('widgets')
				.select('*, program:program_id(id)')
				.eq('id', widgetId)
				.single()

			if (error) throw error
			return data as Widget
		},
		enabled: !!widgetId,
	})

	if (!widget) return null

	return (
		<WidgetPreview
			config={{
				id: widget.id,
				team_id: widget.team_id,
				program_id: widget.program_id,
				name: widget.name,
				header_title: widget.header_title || t('embedWidget.defaultHeaderTitle'),
				position: widget.position as
					| 'middle-left'
					| 'middle-right'
					| 'bottom-left'
					| 'bottom-center'
					| 'bottom-right',
				primaryColor: widget.primary_color,
				backgroundColor: widget.background_color,
				textColor: widget.text_color,
				enable_survey: widget.enable_survey,
				enable_bug_reporting: widget.enable_bug_reporting,
				enable_feature_requests: widget.enable_feature_requests,
				enable_reviews: widget.enable_reviews,
				primary_feedback_type: widget.primary_feedback_type || 'nps',
				question: widget.question,
				customQuestions: widget.custom_questions as {
					id: string
					type: 'text' | 'email' | 'multiple_choice' | 'scale'
					question: string
					required?: boolean
					options?: string[]
					order_index?: number
					min_scale?: number
					max_scale?: number
				}[],
				buttonText: widget.button_text,
				thankYou: widget.thank_you as { title: string; message: string },
				feedbackButton: {
					show: widget.show_feedback_button,
					text: widget.feedback_button_text || t('embedWidget.defaultFeedbackButtonText'),
					color:
						widget.feedback_button_color || widget.primary_color || '#000000',
					textColor: widget.feedback_button_text_color || '#FFFFFF',
				},
				showBranding: widget.show_branding,
			}}
		/>
	)
}
